import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { MetasColaboradorService } from '../services/metasColaborador.service';
import type { AsignarAlcanceRequest, ListaAlcances } from '../types/metas.types';
import { AlertasAlcance, etiquetaAlcance } from './AccesosAvisos';
import { GRUPO_CONFIRMAR } from '../utils/confirmar';
import { AsignarAlcanceForm } from './AsignarAlcanceForm';

interface Props {
  usuario: { id: string; email: string };
}

/**
 * Alcance de metas DENTRO de la edición de una cuenta Indicadores (Usuarios).
 *
 * Vive fuera de `UserForm` a propósito: el formulario tiene sus pruebas y su
 * propio ciclo de guardado; esto se guarda al instante, contra otra ruta
 * (`/metas-colaborador/accesos`), y no debe esperar a «Actualizar usuario».
 */
export const AlcanceMetasSection: React.FC<Props> = ({ usuario }) => {
  const [datos, setDatos] = useState<ListaAlcances | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ sev: 'success' | 'error'; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setDatos(await MetasColaboradorService.listarAlcances());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar, usuario.id]);

  const propios = (datos?.alcances ?? []).filter((a) => a.userId === usuario.id);

  const asignar = async (req: AsignarAlcanceRequest) => {
    setOcupado(true);
    setAviso(null);
    try {
      const r = await MetasColaboradorService.asignarAlcance(req);
      setAviso({ sev: 'success', texto: `Asignado: ${etiquetaAlcance(r)}.` });
      await cargar();
    } catch (e) {
      setAviso({ sev: 'error', texto: (e as Error).message });
    } finally {
      setOcupado(false);
    }
  };

  const quitar = (id: number, texto: string) =>
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Quitar alcance',
      message: `${usuario.email} deja de ser ${texto.toLowerCase()}.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await MetasColaboradorService.revocarAlcance(id);
          setAviso({ sev: 'success', texto: `Retirado: ${texto}.` });
          await cargar();
        } catch (e) {
          setAviso({ sev: 'error', texto: (e as Error).message });
        }
      },
    });

  return (
    <section className="mt-6 border-t border-gray-200 px-6 pb-6 pt-4" data-testid="alcance-metas-section">
      <ConfirmDialog group={GRUPO_CONFIRMAR} />
      <h3 className="text-lg font-semibold text-gray-800">Metas por colaborador</h3>
      <p className="mb-3 text-sm text-gray-500">
        Qué tiendas captura (supervisor) o autoriza (regional) esta cuenta. Se guarda al instante, sin «Actualizar».
      </p>
      {error && <Message severity="error" text={`No se pudieron leer los alcances: ${error}`} className="mb-2 w-full" />}
      {aviso && <Message severity={aviso.sev} text={aviso.texto} className="mb-2 w-full" />}
      {datos && (
        <ul className="mb-4 flex flex-col gap-2">
          {propios.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-200 px-3 py-2">
              <span className="flex flex-col">
                <span>{etiquetaAlcance(a)}</span>
                <AlertasAlcance a={a} />
              </span>
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                aria-label={`Quitar ${etiquetaAlcance(a)}`}
                onClick={() => quitar(a.id, etiquetaAlcance(a))}
              />
            </li>
          ))}
          {!propios.length && <li className="text-sm text-gray-500">Sin alcance: no ve el módulo de metas.</li>}
        </ul>
      )}
      {datos && (
        <AsignarAlcanceForm opciones={datos.opciones} userIdFijo={usuario.id} ocupado={ocupado} onAsignar={asignar} />
      )}
    </section>
  );
};

export default AlcanceMetasSection;
